import { KeyboardEvent, memo, useCallback, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import { defaultGetOptionId, defaultGetOptionName, defaultLoadingMessage } from 'components/helpers/defaultValues'
import { scrollTopIntoView } from 'components/helpers/scrollTopIntoView'
import { switchSelectedOptions } from 'components/helpers/switchSelectedOptions'
import { KeyCodeEnum } from 'enums/keyCodes'
import noop from 'lodash/noop'

import { BaseSelectInput } from './BaseSelectInput'
import { OptionList } from './OptionList'
import { SelectIcons } from './SelectIcons'
import { BaseOptionType, IGeneralSelectProps } from './types'
import { useElementWidth } from './useElementWidth'

import s from './BaseSelect.module.less'

export interface IBaseSelectProps<Option extends BaseOptionType, InputProps extends {}>
  extends IGeneralSelectProps<Option, InputProps> {
  /** Открыт/закрыт выпадающий список */
  isOpen: boolean
  errorLabel?: boolean | string
  errorTestId?: string
  /**
   * Коллбек для получения имени опции
   */
  getOptionName?: (o?: Option) => string
  hasIcon?: boolean
  /**  Коллбек, выполняемый при отрытии выпадающего списка */
  onSetOpen?: (isOpen: boolean) => void
  optionListTestId?: string
  selectTestId?: string
  selectedTestId?: string
}

// Минимальный отступ до рамок окна браузера для списка опций
const VIEWPORT_PADDING = 16

const BaseSelectNoMemo = <Option extends BaseOptionType, InputProps extends {}>({
  tabIndex = 0,
  dataName,
  name,
  classNames: { select: selectCn = {}, option: optionCn } = {},
  hasAutoOptionListWidth = false,
  maxOptionListWidth,
  minOptionListWidth,
  maxOptionListHeight,
  value: selectedValue,
  options,
  placeholder = '',
  error = false,
  errorLabel = false,
  isOpen = false,
  isMobile = false,
  isLoading = false,
  isDisabled = false,
  showLoadingMessage = isLoading,
  onChange = noop,
  onBlur = noop,
  onFocus = noop,
  onScrollOptionList = noop,
  loadingMessage = defaultLoadingMessage,
  groupPositionH,
  groupPositionV,
  CustomSelectInput = BaseSelectInput,
  optionListHeader,
  optionListFooter,
  IconBeforeSelectInput,
  CustomOptionComponent,
  hasDropDownIcon = false,
  hasClearIcon = false,
  hasIcon = true,
  onSetOpen = noop,
  getOptionId = defaultGetOptionId,
  getOptionName = defaultGetOptionName,
  onClear = noop,
  onMobileClose,
  additionalInputProps = {} as InputProps,
  CustomOptionListComponent = OptionList,
  selectTestId,
  selectedTestId,
  optionListTestId,
  errorTestId,
  ...mobileProps
}: IBaseSelectProps<Option, InputProps>) => {
  const [optionHoverIndex, setOptionHoverIndex] = useState(0)
  const optionsMaxIndex = useMemo(() => options.length - 1, [options.length])
  const hoverOptionRef = useRef<HTMLLIElement | null>(null)

  const handleToggleMenu = useCallback(() => {
    if (!isDisabled) {
      onSetOpen(!isOpen)
    }
  }, [isOpen, isDisabled, onSetOpen])

  const handleOpenMenu = useCallback(() => {
    if (!isDisabled) {
      onSetOpen(true)
    }
  }, [isDisabled, onSetOpen])

  const handleCloseMenu = useCallback(() => {
    if (!isDisabled) {
      onSetOpen(false)
      setOptionHoverIndex(0)
    }
  }, [isDisabled, onSetOpen])

  const handleCloseMobile = useCallback(() => {
    if (!isDisabled) {
      onMobileClose?.()
    }

    handleCloseMenu()
  }, [handleCloseMenu, isDisabled, onMobileClose])

  const handleSelectKeyDown = useCallback(
    ({ code }: KeyboardEvent<HTMLElement> | { code: undefined }) => {
      if (!isOpen && code === KeyCodeEnum.ENTER) {
        handleOpenMenu()
      }
    },
    [handleOpenMenu, isOpen]
  )

  const changeSelectedValue = useCallback(
    (options: Option[]) => {
      if (!isDisabled) {
        onChange(options)
      }
    },
    [isDisabled, onChange]
  )

  const scrollToHoverOption = useCallback(() => {
    scrollTopIntoView(hoverOptionRef?.current)
  }, [])

  const setNextHoverIndex = useCallback(() => {
    const nextIndex = optionHoverIndex + 1 <= optionsMaxIndex ? optionHoverIndex + 1 : 0
    setOptionHoverIndex(nextIndex)
  }, [optionHoverIndex, optionsMaxIndex])

  const setPrevHoverIndex = useCallback(() => {
    const nextIndex = optionHoverIndex - 1 >= 0 ? optionHoverIndex - 1 : optionsMaxIndex
    setOptionHoverIndex(nextIndex)
  }, [optionHoverIndex, optionsMaxIndex])

  const handleChangeOption = useCallback(
    (newSelectedOption: Option) => {
      const newSelectedOptions = Array.isArray(selectedValue)
        ? switchSelectedOptions(selectedValue, newSelectedOption, getOptionId)
        : [newSelectedOption]
      onChange(newSelectedOptions)
    },
    [onChange, getOptionId, selectedValue]
  )

  const changeSelectedOption = useCallback(() => {
    if (optionHoverIndex in options) {
      handleChangeOption(options[optionHoverIndex])
    }
  }, [options, optionHoverIndex, handleChangeOption])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent | { code: undefined }) => {
      switch (event.code) {
        case KeyCodeEnum.ESC:
          event.stopPropagation()
          event.nativeEvent.stopImmediatePropagation()
          handleCloseMenu()
          break
        case KeyCodeEnum.TAB:
        case KeyCodeEnum.ENTER:
          event.preventDefault()
          changeSelectedOption()
          break
        case KeyCodeEnum.ARROW_UP:
          event.preventDefault()
          setPrevHoverIndex()
          scrollToHoverOption()
          break
        case KeyCodeEnum.ARROW_DOWN:
          event.preventDefault()
          setNextHoverIndex()
          scrollToHoverOption()
          break
        default:
          break
      }
    },
    [changeSelectedOption, handleCloseMenu, scrollToHoverOption, setNextHoverIndex, setPrevHoverIndex]
  )

  const { ref, width: selectWidth } = useElementWidth({
    needRecalculate: isOpen
  })

  const [wrapperElement, setWrapperElement] = useState<HTMLDivElement | null>(null)

  const wrapperRef = ref.current

  const calculatedMaxOptionListWidth = useMemo(() => {
    if (!isOpen || maxOptionListWidth !== undefined || !hasAutoOptionListWidth || !wrapperRef) {
      return maxOptionListWidth
    }

    const optionListClientRect = wrapperRef.getBoundingClientRect()

    return Math.max(window.innerWidth - optionListClientRect.left - VIEWPORT_PADDING, selectWidth)
  }, [hasAutoOptionListWidth, isOpen, maxOptionListWidth, wrapperRef, selectWidth])

  const hasError = Boolean(error)

  return (
    <>
      {isOpen && !isMobile && <div className={s.curtain} onMouseUp={handleCloseMenu} />}
      <div
        ref={ref}
        data-field-name={name}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
        className={cn(
          s.wrapper,
          { [s.wrapper_open]: isOpen },
          { [s.wrapper_openMobile]: isOpen && isMobile },
          selectCn.wrapper
        )}
      >
        <div
          ref={setWrapperElement}
          data-name={dataName}
          data-test-id={selectTestId}
          id="innerWrapper"
          onBlur={onBlur}
          onClick={handleToggleMenu}
          onFocus={onFocus}
          onKeyDown={handleSelectKeyDown}
          tabIndex={isDisabled ? -1 : tabIndex}
          className={cn(
            s.innerWrapper,
            s.innerWrapper_size_nm,
            groupPositionH && s[`innerWrapper_group-position-h_${groupPositionH}`],
            groupPositionV && s[`innerWrapper_group-position-v_${groupPositionV}`],
            {
              [s.innerWrapper_focused]: isOpen,
              [s.innerWrapper_error]: hasError,
              [s.innerWrapper_focused_error]: hasError && isOpen,
              [s.innerWrapper_disabled]: isDisabled
            },
            selectCn.innerWrapper
          )}
        >
          <div className={cn(s.selectInputWrapper, selectCn.selectInputWrapper)}>
            <CustomSelectInput
              Icon={IconBeforeSelectInput}
              desktopParentElement={wrapperElement}
              getOptionName={getOptionName}
              isDisabled={isDisabled}
              isEmptyOptions={!options.length}
              isOpen={isOpen}
              placeholder={placeholder}
              selectPlaceholderClassName={selectCn.selectPlaceholder}
              selectTextClassName={selectCn.selectText}
              selectedOptions={selectedValue}
              testId={selectedTestId}
              {...additionalInputProps}
            />
          </div>

          <div className={cn(s.iconsWrapper, selectCn.iconsWrapper)}>
            <SelectIcons
              error={error}
              hasDropDownIcon={hasDropDownIcon}
              hasIcon={hasIcon}
              isDisabled={isDisabled}
              isLoading={isLoading}
              isOpen={isOpen}
              onClear={onClear}
              hasClearIcon={
                hasClearIcon &&
                Boolean(Array.isArray(selectedValue) ? selectedValue.length > 0 : Boolean(selectedValue))
              }
            />
          </div>
        </div>
        {errorLabel ? (
          <div className={s.errorLabel} data-test-id={errorTestId}>
            {errorLabel}
          </div>
        ) : (
          ''
        )}
        {isOpen && (
          <CustomOptionListComponent
            {...mobileProps}
            CustomOptionComponent={CustomOptionComponent}
            classNames={optionCn}
            desktopParentElement={wrapperElement}
            getOptionId={getOptionId}
            getOptionName={getOptionName}
            hasAutoOptionListWidth={hasAutoOptionListWidth}
            hasClearIcon={hasClearIcon}
            hoverOptionRef={hoverOptionRef}
            isLoading={isLoading}
            isMobile={isMobile}
            loadingMessage={loadingMessage}
            maxOptionListHeight={maxOptionListHeight}
            maxOptionListWidth={calculatedMaxOptionListWidth}
            minOptionListWidth={minOptionListWidth}
            onChangeForDesktop={handleChangeOption}
            onChangeForMobile={changeSelectedValue}
            onClear={onClear}
            onCloseMenu={handleCloseMobile}
            onScrollOptionList={onScrollOptionList}
            onSetOptionHoverIndex={setOptionHoverIndex}
            optionHoverIndex={optionHoverIndex}
            optionListFooter={optionListFooter}
            optionListHeader={optionListHeader}
            options={options}
            selectWidth={selectWidth}
            showLoadingMessage={showLoadingMessage}
            testId={optionListTestId}
            value={selectedValue}
          />
        )}
      </div>
    </>
  )
}

export const BaseSelect = memo(BaseSelectNoMemo) as typeof BaseSelectNoMemo
